/**
 * AddEntityLinkForm — link a document to this object, from the object's side
 * (FR-017.7). The mirror of the document page's AddDocumentLinkForm: there
 * the document is fixed and the farmer picks an object; here the object is
 * fixed and the farmer picks a document.
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { linkWriteErrorMessage } from '../../../components/twinLinks/linkErrors';
import { LinkTypeFields, linkPayload, linkTypeComplete } from '../../../components/twinLinks/LinkTypeFields';
import { twinService } from '../../../services/twinService';

export interface DocumentChoice {
    document_id: string;
    label: string;
    document_date: string | null;
}

interface AddEntityLinkFormProps {
    entityType: string;
    entityId: string;
    searchDocuments: (q: string) => Promise<DocumentChoice[]>;
    onCreated: () => void;
    onCancel: () => void;
}

/** Fewer characters than this match half the archive and say nothing. */
const MIN_QUERY = 2;

export const AddEntityLinkForm: React.FC<AddEntityLinkFormProps> = ({
    entityType,
    entityId,
    searchDocuments,
    onCreated,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [query, setQuery] = React.useState('');
    const [picked, setPicked] = React.useState<DocumentChoice | null>(null);
    const [linkType, setLinkType] = React.useState('generic');
    const [validUntil, setValidUntil] = React.useState('');

    const trimmed = query.trim();
    const hits = useQuery({
        queryKey: ['documents', 'link-picker', trimmed],
        queryFn: () => searchDocuments(trimmed),
        enabled: trimmed.length >= MIN_QUERY && picked === null,
    });

    const create = useMutation({
        mutationFn: () =>
            twinService.createLink({
                document_id: picked!.document_id,
                target_type: entityType,
                target_id: entityId,
                link_type: linkType,
                payload: linkPayload(linkType, validUntil),
            }),
        onSuccess: onCreated,
    });

    const canSubmit = picked !== null && linkTypeComplete(linkType, validUntil) && !create.isPending;

    return (
        <form
            className="documents-panel__add"
            aria-label={t('twinLinks.objectSide.addLabel')}
            onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) create.mutate();
            }}
        >
            {picked ? (
                <p className="documents-panel__picked">
                    {picked.label}{' '}
                    <button type="button" onClick={() => setPicked(null)}>
                        {t('twinLinks.form.change')}
                    </button>
                </p>
            ) : (
                <>
                    <label>
                        {t('twinLinks.objectSide.searchDocument')}
                        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
                    </label>
                    {hits.isError && <p>{t('twinLinks.objectSide.searchFailed')}</p>}
                    {hits.data && hits.data.length === 0 && <p>{t('twinLinks.form.noMatch')}</p>}
                    {hits.data && hits.data.length > 0 && (
                        <ul aria-label={t('twinLinks.form.matches')}>
                            {hits.data.map((d) => (
                                <li key={d.document_id}>
                                    <button type="button" onClick={() => setPicked(d)}>
                                        {d.label}
                                        {d.document_date ? ` · ${d.document_date}` : ''}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}

            <LinkTypeFields
                linkType={linkType}
                onLinkType={setLinkType}
                validUntil={validUntil}
                onValidUntil={setValidUntil}
            />

            <button type="submit" disabled={!canSubmit}>
                {t('twinLinks.form.submit')}
            </button>
            <button type="button" onClick={onCancel}>
                {t('twinLinks.form.cancel')}
            </button>

            {create.isError && (
                <p role="alert" className="documents-panel__error">
                    {linkWriteErrorMessage(create.error)}
                </p>
            )}
        </form>
    );
};

export default AddEntityLinkForm;
